import { FormEvent } from "react";
import { Head, Link, useForm } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type WorkingHour = {
  day: string;
  opens_at: string;
  closes_at: string;
  closed: boolean;
};

type AgencyFormData = {
  name: string;
  description: string;
  services: string[];
  working_hours: WorkingHour[];
  facebook_url: string;
  instagram_url: string;
  x_url: string;
  website_url: string;
  whatsapp_number: string;
  phone_number: string;
  address: string;
  status: string;
  trusted: boolean;
};

interface AgencyProps extends Omit<AgencyFormData, "trusted"> {
  id: number;
  trusted?: boolean;
}

interface Props {
  agency: AgencyProps;
}

const WEEK_DAYS = [
  { key: "sunday", label: "الأحد" },
  { key: "monday", label: "الاثنين" },
  { key: "tuesday", label: "الثلاثاء" },
  { key: "wednesday", label: "الأربعاء" },
  { key: "thursday", label: "الخميس" },
  { key: "friday", label: "الجمعة" },
  { key: "saturday", label: "السبت" },
];

const defaultWorkingHours = (): WorkingHour[] =>
  WEEK_DAYS.map(({ key }) => ({
    day: key,
    opens_at: key === "friday" ? "" : "09:00",
    closes_at: key === "friday" ? "" : "17:00",
    closed: key === "friday",
  }));

const SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: "trading", label: "بيع العقارات" },
  { value: "rental", label: "تأجير العقارات" },
  { value: "legal_consulting", label: "استشارات قانونية" },
  { value: "property_management", label: "إدارة ممتلكات" },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pending_verification", label: "قيد التحقق" },
  { value: "active", label: "مفعلة" },
  { value: "inactive", label: "غير فعالة" },
  { value: "suspended", label: "موقوفة" },
];

const normalizeServices = (input: unknown): string[] => {
  if (Array.isArray(input)) return input;
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // fall through to splitting
    }
    return input
      .split(/[,،]/)
      .map((service) => service.trim())
      .filter(Boolean);
  }
  return [];
};

export default function Edit({ agency }: Props) {
  const { data, setData, put, processing, errors } = useForm<AgencyFormData>({
    name: agency.name || "",
    description: agency.description || "",
    services: normalizeServices(agency.services),
    working_hours:
      agency.working_hours && agency.working_hours.length > 0
        ? agency.working_hours
        : defaultWorkingHours(),
    facebook_url: agency.facebook_url || "",
    instagram_url: agency.instagram_url || "",
    x_url: agency.x_url || "",
    website_url: agency.website_url || "",
    whatsapp_number: agency.whatsapp_number || "",
    phone_number: agency.phone_number || "",
    address: agency.address || "",
    status: agency.status || "pending_verification",
    trusted: agency.trusted ?? false,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    put(`/desk/agencies/${agency.id}`);
  };

  const renderFieldError = (field: keyof AgencyFormData) => {
    const error = errors[field];
    if (!error) return null;
    return <p className="text-sm text-destructive">{error}</p>;
  };

  const handleWorkingHourChange = (
    day: string,
    field: keyof Omit<WorkingHour, "day" | "closed">,
    value: string
  ) => {
    setData(
      "working_hours",
      data.working_hours.map((entry) =>
        entry.day === day
          ? {
              ...entry,
              [field]: value,
            }
          : entry
      )
    );
  };

  const toggleClosed = (day: string, checked: boolean) => {
    setData(
      "working_hours",
      data.working_hours.map((entry) =>
        entry.day === day
          ? {
              ...entry,
              closed: checked,
              opens_at: checked ? "" : entry.opens_at || "09:00",
              closes_at: checked ? "" : entry.closes_at || "17:00",
            }
          : entry
      )
    );
  };

  const toggleService = (value: string, checked: boolean) => {
    if (checked) {
      setData("services", Array.from(new Set([...data.services, value])));
    } else {
      setData(
        "services",
        data.services.filter((service) => service !== value)
      );
    }
  };

  const canMarkTrusted = data.status === "active";

  return (
    <>
      <Head title={`تعديل الوكالة ${agency.name}`} />

      <Card>
        <CardHeader>
          <CardTitle>تعديل الوكالة</CardTitle>
          <CardDescription>قم بحفظ بيانات الوكالة ثم احفظ التغييرات</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">اسم الوكالة</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(event) => setData("name", event.target.value)}
                  required
                />
                {renderFieldError("name")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">رقم الهاتف</Label>
                <Input
                  id="phone_number"
                  value={data.phone_number}
                  onChange={(event) => setData("phone_number", event.target.value)}
                  inputMode="tel"
                />
                {renderFieldError("phone_number")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">الحالة</Label>
                <Select
                  value={data.status}
                  onValueChange={(value) => {
                    setData("status", value);
                    if (value !== "active") {
                      setData("trusted", false);
                    }
                  }}
                  defaultValue={data.status}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="اختر الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("status")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="whatsapp_number">رقم الواتساب</Label>
                <Input
                  id="whatsapp_number"
                  value={data.whatsapp_number}
                  onChange={(event) => setData("whatsapp_number", event.target.value)}
                  inputMode="tel"
                />
                {renderFieldError("whatsapp_number")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="facebook_url">رابط فيسبوك</Label>
                <Input
                  id="facebook_url"
                  value={data.facebook_url}
                  onChange={(event) => setData("facebook_url", event.target.value)}
                  type="url"
                />
                {renderFieldError("facebook_url")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagram_url">رابط إنستغرام</Label>
                <Input
                  id="instagram_url"
                  value={data.instagram_url}
                  onChange={(event) => setData("instagram_url", event.target.value)}
                  type="url"
                />
                {renderFieldError("instagram_url")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="x_url">رابط X</Label>
                <Input
                  id="x_url"
                  value={data.x_url}
                  onChange={(event) => setData("x_url", event.target.value)}
                  type="url"
                />
                {renderFieldError("x_url")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url">رابط الموقع الإلكتروني</Label>
                <Input
                  id="website_url"
                  value={data.website_url}
                  onChange={(event) => setData("website_url", event.target.value)}
                  type="url"
                />
                {renderFieldError("website_url")}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">العنوان</Label>
              <Textarea
                id="address"
                value={data.address}
                onChange={(event) => setData("address", event.target.value)}
                rows={3}
              />
              {renderFieldError("address")}
            </div>

            <div className="space-y-3">
              <Label>الخدمات</Label>
              <div className="grid gap-2 md:grid-cols-2">
                {SERVICE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2"
                  >
                    <Checkbox
                      checked={data.services.includes(option.value)}
                      onCheckedChange={(checked) => toggleService(option.value, Boolean(checked))}
                    />
                    <span className="text-sm text-foreground">{option.label}</span>
                  </label>
                ))}
              </div>
              {renderFieldError("services")}
            </div>

            <div className="space-y-4">
              <div>
                <Label>ساعات العمل</Label>
                <p className="text-sm text-muted-foreground">حدث ساعات العمل لكل يوم.</p>
              </div>
              <div className="grid gap-4">
                {WEEK_DAYS.map(({ key, label }) => {
                  const entry = data.working_hours.find((item) => item.day === key) || {
                    day: key,
                    opens_at: "",
                    closes_at: "",
                    closed: false,
                  };

                  return (
                    <div
                      key={key}
                      className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/40 p-3 md:grid-cols-5 md:items-center"
                    >
                      <div className="text-sm font-medium text-foreground">{label}</div>
                      <div className="md:col-span-2 md:flex md:items-center md:gap-3">
                        <div className="space-y-1 md:flex-1">
                          <Label
                            htmlFor={`${key}-opens_at`}
                            className="text-xs text-muted-foreground"
                          >
                            من
                          </Label>
                          <Input
                            id={`${key}-opens_at`}
                            type="time"
                            value={entry.opens_at || ""}
                            disabled={entry.closed}
                            onChange={(event) =>
                              handleWorkingHourChange(key, "opens_at", event.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1 md:flex-1">
                          <Label
                            htmlFor={`${key}-closes_at`}
                            className="text-xs text-muted-foreground"
                          >
                            إلى
                          </Label>
                          <Input
                            id={`${key}-closes_at`}
                            type="time"
                            value={entry.closes_at || ""}
                            disabled={entry.closed}
                            onChange={(event) =>
                              handleWorkingHourChange(key, "closes_at", event.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`${key}-closed`}
                          checked={entry.closed}
                          onCheckedChange={(checked) => toggleClosed(key, Boolean(checked))}
                        />
                        <Label htmlFor={`${key}-closed`} className="text-sm">
                          مغلق
                        </Label>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderFieldError("working_hours")}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">وصف الوكالة</Label>
              <Textarea
                id="description"
                value={data.description}
                onChange={(event) => setData("description", event.target.value)}
              />
              {renderFieldError("description")}
            </div>

            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-3">
              <Checkbox
                id="trusted"
                checked={canMarkTrusted ? data.trusted : false}
                disabled={!canMarkTrusted}
                onCheckedChange={(checked) => {
                  if (canMarkTrusted) {
                    setData("trusted", Boolean(checked));
                  }
                }}
              />
              <div className="space-y-0.5">
                <Label htmlFor="trusted" className="text-sm font-medium">
                  وكالة موثوقة
                </Label>
                <p className="text-xs text-muted-foreground">
                  {canMarkTrusted
                    ? "العقارات التي تنشئها هذه الوكالة تُنشر مباشرة دون انتظار موافقة الإدارة"
                    : "يجب تفعيل الوكالة أولاً قبل تعيينها كموثوقة"}
                </p>
              </div>
            </div>
            {renderFieldError("trusted")}

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" asChild>
                <Link href="/desk/agencies">إلغاء</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "جاري الحفظ..." : "حفظ التغييرات"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
